// src/lib/meta-pixel.ts
// Meta (Facebook) Pixel — the ads-side counterpart to lib/analytics.ts.
//
// Gated on REACT_APP_META_PIXEL_ID: without the env var nothing is injected and
// no data is collected. Like GA, initMetaPixel() is deliberately NOT called from
// index.tsx — <CookieConsent /> calls it on accept (or immediately for a
// returning visitor who already accepted), so the pixel never loads before the
// visitor has answered the banner. GPC always wins via hasTrackingConsent().
// See docs/traffic-attribution.md and lib/consent.ts.
//
// TWO DELIBERATE DEPARTURES FROM META'S COPY-PASTE SNIPPET:
//
//  1. autoConfig is turned OFF before init. Meta's default "Automatic Advanced
//     Matching" scrapes form fields on the page — emails, phone numbers, names —
//     and sends hashed copies to Meta. On the checkout page that means customer
//     PII leaving the site automatically. autoConfig:false also disables Meta's
//     automatic button/form event detection. Everything this pixel reports is
//     therefore explicit and listed in this file. Do not remove this line.
//
//  2. No <noscript> tracking <img>. Meta's snippet includes one that fires
//     unconditionally on load, which is exactly the ungated-third-party leak the
//     consent gate exists to prevent (cf. the Square mailing-list iframe). A
//     no-JS visitor can't reach the consent banner either, so there is nothing
//     to fall back to.
//
// CONTENT IDS: Meta matches events to catalog products by content id. Ours are
// the Square catalog VARIATION SKUs — the same value exported as the `id` column
// by scripts/export-meta-catalog.js. Passing anything else (an item id, a slug)
// silently breaks catalog matching and retargeting, and surfaces in Events
// Manager as "content IDs not matching products in your catalog".
//
// MONEY: repo convention is integer cents; Meta wants decimal units. Convert at
// this boundary (cents / 100) and nowhere else.

type FbqFn = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[][];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
  }
}

const pixelId = process.env.REACT_APP_META_PIXEL_ID;

/** Cents → Meta's decimal value. */
const toValue = (cents: number): number => Number((cents / 100).toFixed(2));

const fbq = (...args: unknown[]): void => {
  if (!pixelId || !window.fbq) return;
  window.fbq(...args);
};

/**
 * Inject fbevents.js and initialize. Safe to call repeatedly — the window.fbq
 * guard makes re-entry a no-op, which matters because <CookieConsent /> can
 * re-evaluate when the choice changes in another tab.
 */
export const initMetaPixel = (): void => {
  if (!pixelId || window.fbq) return;

  // Meta's loader stub: queues calls made before fbevents.js finishes loading,
  // then replays them once callMethod exists.
  const stub: FbqFn = function stubbedFbq(...args: unknown[]) {
    stub.callMethod
      ? stub.callMethod.apply(stub, args)
      : stub.queue!.push(args);
  } as FbqFn;
  stub.queue = [];
  stub.loaded = true;
  stub.version = "2.0";
  stub.push = stub;
  window.fbq = stub;
  window._fbq = stub;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  // MUST precede init — see the autoConfig note at the top of this file.
  stub("set", "autoConfig", false, pixelId);
  stub("init", pixelId);
  stub("track", "PageView");
};

/**
 * Page view for client-side route changes. No-op until the pixel is initialized,
 * so it is safe to call unconditionally from the router (mirrors trackPageView).
 */
export const trackPixelPageView = (): void => {
  fbq("track", "PageView");
};

/** Product detail view. `sku` is the Square variation SKU (the catalog id). */
export const trackViewContent = (params: {
  sku: string;
  name: string;
  priceCents: number;
}): void => {
  fbq("track", "ViewContent", {
    content_type: "product",
    content_ids: [params.sku],
    content_name: params.name,
    value: toValue(params.priceCents),
    currency: "USD",
  });
};

export const trackAddToCart = (params: {
  sku: string;
  name: string;
  priceCents: number;
  quantity?: number;
}): void => {
  fbq("track", "AddToCart", {
    content_type: "product",
    content_ids: [params.sku],
    content_name: params.name,
    contents: [{ id: params.sku, quantity: params.quantity ?? 1 }],
    value: toValue(params.priceCents * (params.quantity ?? 1)),
    currency: "USD",
  });
};

export const trackInitiateCheckout = (params: {
  skus: string[];
  totalCents: number;
  numItems: number;
}): void => {
  fbq("track", "InitiateCheckout", {
    content_type: "product",
    content_ids: params.skus,
    num_items: params.numItems,
    value: toValue(params.totalCents),
    currency: "USD",
  });
};

/**
 * Completed purchase — the event ad attribution actually keys off.
 *
 * `orderId` (the Square order id) is passed as eventID so Meta can deduplicate.
 * That matters twice: a receipt page the customer reloads must not count twice,
 * and if a server-side Conversions API is ever added it can send the same
 * eventID for the same order instead of double-counting. Never make this random.
 */
export const trackPurchase = (params: {
  orderId: string;
  skus: string[];
  totalCents: number;
  numItems: number;
}): void => {
  fbq(
    "track",
    "Purchase",
    {
      content_type: "product",
      content_ids: params.skus,
      num_items: params.numItems,
      value: toValue(params.totalCents),
      currency: "USD",
    },
    { eventID: params.orderId },
  );
};
